import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import React from 'react';
import { RouteComponentProps } from 'react-router';
import { PolicyAttachmentsList } from '~frontend-common/components/policy-attachments-list';
import { PolicyCard } from '~frontend-common/components/policy-card';
import { SentryErrorBox } from '~frontend-common/components/ui';
import { useHistory } from '~frontend-common/hooks/use-history';
import { useTranslator } from '~frontend-common/i18n/translator';
import { Box } from '~ui-kit/components/box/box';
import { LoadingSpinner } from '~ui-kit/components/loading-spinner';
import { PageContentContainer } from '~web-portal/components/page-content-container';
import { PageHeader } from '~web-portal/components/page-header';
import { policyService } from '~web-portal/services';
import { Paths } from '~web-portal/variables/paths';

export function PolicyDocumentsPage({ match }: RouteComponentProps<{ policyId: string }>) {
  const { policyId } = match.params;

  const history = useHistory();
  const translator = useTranslator();
  const { tl } = translator;

  const policyQuery = useQuery({
    queryKey: ['policy', policyId],
    cacheTime: 60 * 1000,
    queryFn: () => policyService.getPolicy(policyId),
  });

  const handleBack = () => {
    history.push(Paths.policies);
  };

  const renderContent = () => {
    if (policyQuery.isLoading) {
      return (
        <Box flexDirection="column" justifyContent="center" minHeight="35vh">
          <LoadingSpinner />
        </Box>
      );
    }

    if (policyQuery.isError) {
      return <SentryErrorBox error={policyQuery.error} translator={translator} my={3} />;
    }

    const policy = policyQuery.data;
    if (isNil(policy)) {
      return null;
    }

    return (
      <>
        <PolicyCard policy={policy} simple />
        <PolicyAttachmentsList policy={policy} />
      </>
    );
  };

  return (
    <Box data-id="policy-documents-page" flexDirection="column" flex="1 1 auto">
      <PageContentContainer>
        <PageHeader
          backButtonOnClick={handleBack}
          title={tl('policyPages.documents.title')}
          subtitle={tl('policyPages.documents.subtitle')}
          mb={4}
        />

        {renderContent()}
      </PageContentContainer>
    </Box>
  );
}
